import { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import { styled, alpha } from '@mui/material/styles';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';

interface ShareButtonProps {
  title: string;
  text?: string;
  url?: string;
  variant?: 'icon' | 'contained';
}

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(4px)',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
  },
}));

const WhatsAppMenuItem = styled(MenuItem)({
  '& .MuiListItemIcon-root': {
    color: '#25D366',
  },
});

const TwitterMenuItem = styled(MenuItem)({
  '& .MuiListItemIcon-root': {
    color: '#1DA1F2',
  },
});

const FacebookMenuItem = styled(MenuItem)({
  '& .MuiListItemIcon-root': {
    color: '#1877F2',
  },
});

export function ShareButton({ title, text, url, variant = 'icon' }: ShareButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const shareUrl = url || window.location.href;
  const shareText = text || title;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Try native share first on mobile
    if (navigator.share) {
      navigator
        .share({
          title,
          text: shareText,
          url: shareUrl,
        })
        .catch(() => {
          // User cancelled or error - show menu instead
          setAnchorEl(event.currentTarget);
        });
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
    window.open(whatsappUrl, '_blank');
    handleClose();
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank');
    handleClose();
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank');
    handleClose();
  };

  const handleEmailShare = () => {
    const emailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
    window.location.href = emailUrl;
    handleClose();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSnackbar('Link copied to clipboard!');
    } catch {
      showSnackbar('Failed to copy link');
    }
    handleClose();
  };

  return (
    <>
      {variant === 'icon' ? (
        <StyledIconButton onClick={handleClick} aria-label="Share">
          <ShareIcon />
        </StyledIconButton>
      ) : (
        <IconButton onClick={handleClick} aria-label="Share" color="inherit">
          <ShareIcon />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <WhatsAppMenuItem onClick={handleWhatsAppShare}>
          <ListItemIcon>
            <WhatsAppIcon />
          </ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </WhatsAppMenuItem>

        <TwitterMenuItem onClick={handleTwitterShare}>
          <ListItemIcon>
            <TwitterIcon />
          </ListItemIcon>
          <ListItemText>Twitter</ListItemText>
        </TwitterMenuItem>

        <FacebookMenuItem onClick={handleFacebookShare}>
          <ListItemIcon>
            <FacebookIcon />
          </ListItemIcon>
          <ListItemText>Facebook</ListItemText>
        </FacebookMenuItem>

        <MenuItem onClick={handleEmailShare}>
          <ListItemIcon>
            <EmailIcon />
          </ListItemIcon>
          <ListItemText>Email</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <ContentCopyIcon />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
